import { useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import {
  AButton,
  ARate,
  ATitle,
  Loading,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  EyeOutlined,
  FormOutlined,
  RedoOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Table } from 'antd';
import { IFormLog, ISchedule } from '../../../shared/interfaces';
import api from '../../../services/api';
import EvolutionCreate from './Create';
import { formatDate } from '../../../shared/utils/format-date';
import { ObservationText } from './styles';
import { useAuth } from '../../../hooks/auth';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import { Line, getElementAtEvent } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const FormClientEvolution: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [clientFormlogs, setClientFormLogs] = useState<IFormLog[]>([]);
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [clientFormlog, setClientFormLog] = useState<IFormLog | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const chartRef = useRef<any>();

  const loadHistoric = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/clients-forms-logs/list/${id}`)
      .then((response) => {
        setClientFormLogs(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadSchedules = useCallback(async () => {
    await api
      .get(`/schedules/select/${id}`)
      .then((response) => {
        setSchedules(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      });
  }, []);

  const renderModalCreateEvolution = () => {
    if (modalCreate) {
      return (
        <EvolutionCreate
          open={modalCreate}
          close={() => setModalCreate(false)}
          client_form_id={id}
          setClientFormLogs={setClientFormLogs}
          clientFormLog={clientFormlog}
          schedules={schedules}
        />
      );
    }
  };

  const options = {
    responsive: true,
    scales: {
      y: {
        max: 10,
        min: 0,
      },
    },
  };

  const data: ChartData<any> = {
    labels: clientFormlogs.map((log) => formatDate(log.date)),
    datasets: [
      {
        label: 'Avaliação',
        data: clientFormlogs.map((log) => log.rank),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  };

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (getElementAtEvent(chartRef.current, event).length > 0) {
        const dataset = getElementAtEvent(chartRef.current, event);
        const index = dataset[0]?.index;
        if (clientFormlogs[index]) {
          setClientFormLog(clientFormlogs[index]);
          setModalCreate(true);
        }
      }
    },
    [clientFormlogs],
  );

  useEffect(() => {
    loadHistoric();
    loadSchedules();
  }, [id]);

  if (loading) return <Loading load />;

  return (
    <PageLayout>
      <PageIntro
        badge="Prontuários"
        icon={<RiseOutlined />}
        title="Evolução do prontuário"
        description="Acompanhe evolução clínica, gráfico de evolução e histórico em uma única visualização padronizada."
      />
      <PagePanel>
        <Row style={{ display: 'flex', justifyContent: 'end' }}>
          <MenuOptions
            menu_items={[
              {
                label: 'Atualizar',
                key: '1',
                icon: <RedoOutlined />,
                onClick: () => loadHistoric(),
              },
              permissionFind([PermissionActions.formsUpdate])
                ? {
                    label: 'Cadastrar',
                    key: '2',
                    icon: <FormOutlined />,
                    onClick: () => {
                      setClientFormLog(null);
                      setModalCreate(true);
                    },
                  }
                : null,
            ]}
            showButtonBack
          />
        </Row>
        <Row>
          <ATitle>Grafico</ATitle>
          <Line
            ref={chartRef}
            options={options}
            data={data}
            width={400}
            height={80}
            onClick={handleClick}
          />
        </Row>
        <Row>
          <ATitle>Histórico</ATitle>
          <Table
            scroll={{ y: 310 }}
            columns={[
              {
                title: 'Data',
                dataIndex: 'date',
                render: (value: string) => (
                  <ObservationText>{formatDate(value)}</ObservationText>
                ),
                width: 110,
              },
              {
                title: 'Hora',
                dataIndex: 'time',
                render: (value: string) => (
                  <ObservationText>{value}</ObservationText>
                ),
                width: 100,
              },
              {
                title: 'Descrição',
                dataIndex: 'observation',
                render: (value: string) => (
                  <ObservationText>{value}</ObservationText>
                ),
              },
              {
                title: 'Rank',
                dataIndex: 'rank',
                width: '35%',
                render: (value: number) => {
                  return <ARate defaultValue={value} count={10} disabled />;
                },
              },
              {
                title: 'Opção',
                render: (cell: IFormLog) => {
                  return (
                    <AButton
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setClientFormLog(cell);
                        setModalCreate(true);
                      }}
                    >
                      Visualizar
                    </AButton>
                  );
                },
              },
            ]}
            dataSource={clientFormlogs}
            pagination={false}
          />
        </Row>
      </PagePanel>
      {renderModalCreateEvolution()}
    </PageLayout>
  );
};

export default FormClientEvolution;
